import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import GmModal from 'app/shared/modal/modal';
import { OptionsModal } from 'app/shared/option-modal';
import ListCreationForm from 'app/member/modules/mailing-lists/create/create';
import ImportContact from 'app/member/modules/contacts/import/import';
import { SenderIDRequestForm } from 'app/member/modules/messaging/sender-id/form';
import { useCampaignService } from 'hooks/mailing/campaign';
import { useTransactionalService } from 'hooks/mailing/transactional';
import styles from './checklist.module.css';

const ChecklistSkeleton = () => (
  <div className={styles.wrapper}>
    <div className={styles.progressHeader}>
      <div className={styles.progressTop}>
        <Skeleton width={200} height={16} />
        <Skeleton width={60} height={16} />
      </div>
      <Skeleton height={6} borderRadius={3} style={{ marginTop: 8 }} />
    </div>
    <div className={styles.steps}>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className={styles.step}>
          <div className={styles.stepLeft}>
            <Skeleton circle width={20} height={20} />
          </div>
          <div className={styles.stepBody}>
            <Skeleton width={180} height={14} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const STEP_CONTENT = {
  create_audience: {
    title: 'Create your first audience',
    description: 'Audiences are where your contacts live. Create one before importing.',
    cta_label: 'Create audience',
    cta_action: 'modal',
    cta_modal: 'create_audience',
    tag: null,
    encouragement: 'Audience created. Your contacts have a home.'
  },
  import_contacts: {
    title: 'Import contacts into your audience',
    description: 'Upload a CSV or migrate from your previous platform.',
    cta_label: 'Import contacts',
    cta_action: 'modal',
    cta_modal: 'import_contacts',
    tag: null,
    encouragement: 'Contacts imported. Your audience is ready.'
  },
  setup_dns: {
    title: 'Set up your domain',
    description: 'Send from your brand email, not go-mailer@mailr.xyz.',
    cta_label: 'Set up domain',
    cta_destination: '/settings/email',
    tag: { label: 'Recommended before first send', style: 'warn' },
    encouragement: 'Domain configured. Your emails are now trusted.'
  },
  send_campaign: {
    title: 'Send your first campaign',
    description: "Complete the steps above and you're ready.",
    cta_label: 'Create campaign',
    cta_action: 'modal',
    cta_modal: 'create_campaign',
    tag: null,
    encouragement: "First campaign sent. You're live."
  },
  request_sms_sender: {
    title: 'Request your SMS Sender ID',
    description:
      'Request now — approval takes up to 5 business days. You can complete other steps while you wait.',
    cta_label: 'Request Sender ID',
    cta_action: 'modal',
    cta_modal: 'sender_id',
    tag: { label: '5-day approval — start now', style: 'warn' },
    encouragement: 'Sender ID requested. Approval takes up to 5 business days.'
  },
  install_push_sdk: {
    title: 'Install the Push SDK',
    description:
      'The SDK must be installed in your app before push notifications can be delivered. Share the setup guide with your developer.',
    cta_label: 'Copy setup guide link',
    cta_action: 'copy',
    cta_copy_value: 'go-mailer.com/developers/guides/push',
    tag: { label: 'Your developer handles this', style: 'dev' },
    encouragement: 'Push SDK installed. Notifications are ready to send.'
  },
  create_transactional_template: {
    title: 'Create a transactional template',
    description: 'Build your email design — your app references it by code when sending.',
    cta_label: 'Create template',
    cta_action: 'modal',
    cta_modal: 'create_transactional',
    tag: null,
    encouragement: 'Template ready. Your developer can reference it by code now.'
  },
  generate_api_key: {
    title: 'Generate your API key',
    description: 'Settings → Developer. Keep it secure — never expose it in client-side code.',
    cta_label: 'Generate key',
    cta_destination: '/settings/developer',
    tag: null,
    encouragement: 'API key generated. Share it securely with your development team.'
  },
  send_test_transactional: {
    title: 'Send a test email',
    description: 'Make your first API call and confirm delivery.',
    cta_label: 'View send guide',
    cta_destination: 'go-mailer.com/developers/reference/transactionals',
    tag: null,
    encouragement: 'First transactional email sent. Your integration is working.'
  },
  enable_retry_on_failure: {
    title: 'Enable retry on failure',
    description: 'Critical for OTPs and payment confirmations — ensures they always arrive.',
    cta_label: 'Enable on your template',
    cta_destination: '/transactional',
    tag: null,
    encouragement: 'Retry enabled. Your critical emails will always get through.'
  }
};

const TAG_STYLES = {
  warn: styles.tagWarn,
  dev: styles.tagDev
};

const ChecklistStep = ({ step, is_last, onOpenModal }) => {
  const navigate = useNavigate();
  const content = STEP_CONTENT[step.id];
  const [show_encouragement, setShowEncouragement] = useState(false);
  const [was_active, setWasActive] = useState(step.state === 'active');

  useEffect(() => {
    if (was_active && step.state === 'complete') {
      setShowEncouragement(true);
      const t = setTimeout(() => setShowEncouragement(false), 3000);
      return () => clearTimeout(t);
    }
    if (step.state === 'active') setWasActive(true);
  }, [step.state, was_active]);

  if (!content) return null;

  const handleCta = () => {
    if (content.cta_action === 'modal') {
      onOpenModal(content.cta_modal);
      return;
    }
    if (content.cta_action === 'copy') {
      navigator.clipboard.writeText(content.cta_copy_value);
      return;
    }
    if (!content.cta_destination) return;
    if (content.cta_destination.startsWith('/')) {
      navigate(content.cta_destination);
    } else {
      window.open(`https://${content.cta_destination}`, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={`${styles.step} ${styles[step.state]}`}>
      <div className={styles.stepLeft}>
        <div className={styles.circle}>
          {step.state === 'complete' && <span className={styles.check}>✓</span>}
          {step.state === 'active' && <span className={styles.activeDot} />}
        </div>
        {!is_last && <div className={styles.connector} />}
      </div>
      <div className={styles.stepBody}>
        <p className={styles.stepTitle}>{content.title}</p>
        {show_encouragement && <p className={styles.encouragement}>{content.encouragement}</p>}
        {step.state === 'active' && (
          <>
            {content.tag && (
              <span className={`${styles.tag} ${TAG_STYLES[content.tag.style] || ''}`}>
                {content.tag.label}
              </span>
            )}
            <p className={styles.stepDescription}>{content.description}</p>
            {content.cta_label && (
              <button className={styles.ctaButton} onClick={handleCta}>
                {content.cta_label}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const SetupChecklist = ({
  steps,
  progress_pct,
  completed_count,
  total_count,
  header_message,
  loading = false,
  onRefresh = () => {}
}) => {
  const { creationOptions: campaign_options } = useCampaignService();
  const { creationOptions: transactional_options } = useTransactionalService();
  const [open_modal, setOpenModal] = useState(null);

  const closeModal = () => {
    setOpenModal(null);
    onRefresh();
  };

  if (loading) return <ChecklistSkeleton />;

  const rendered = steps.map((step, i) => (
    <ChecklistStep
      key={step.id}
      step={step}
      is_last={i === steps.length - 1}
      onOpenModal={setOpenModal}
    />
  ));

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.progressHeader}>
          <div className={styles.progressTop}>
            <span className={styles.progressTitle}>Your account is almost ready</span>
            <span className={styles.progressCount}>
              {completed_count} of {total_count} done
            </span>
          </div>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${progress_pct}%` }} />
          </div>
        </div>
        {header_message && <p className={styles.subHeader}>{header_message}</p>}
        <div className={styles.steps}>{rendered}</div>
      </div>

      <GmModal
        show_modal={open_modal === 'create_audience'}
        show_header
        title="New Audience"
        onClose={closeModal}
      >
        <ListCreationForm closeModal={closeModal} />
      </GmModal>

      <GmModal
        show_modal={open_modal === 'import_contacts'}
        show_header
        title="Import Contacts"
        onClose={closeModal}
      >
        <ImportContact closeModal={closeModal} />
      </GmModal>

      <GmModal
        show_modal={open_modal === 'create_campaign'}
        show_header
        title="New Campaign"
        onClose={closeModal}
      >
        <OptionsModal options={campaign_options} />
      </GmModal>

      <GmModal
        show_modal={open_modal === 'create_transactional'}
        show_header
        title="New Template"
        onClose={closeModal}
      >
        <OptionsModal options={transactional_options} />
      </GmModal>

      <GmModal
        show_modal={open_modal === 'sender_id'}
        show_header
        title="Request Sender ID"
        onClose={closeModal}
      >
        <SenderIDRequestForm closeModal={closeModal} />
      </GmModal>
    </>
  );
};
